import * as THREE from 'three';
import { G, M, TINT, HP, K_BEV, glowMat, mesh, bevel, plate, rib, mergeBones, stages, boneG } from './kit';
/* ============================================================
   WAAGE 天秤 —— BOSS・不對稱
   ============================================================

   剪影：桅杆＋一盾一砲的橫樑
   階段：'P1 平衡・盾永遠擋在砲的另一側','P2 盾落・整座往砲側傾倒','P3 雙臂垂下・橫樑變連枷'

   由 `tools/port-bosses.mjs` 從 `enemy_sample_ani.html` 機械移植，
   **不要手改這個檔**：要改造型請改原型再重跑移植器，否則 1:1 就斷了。
   驗證：`node tools/port-bosses.mjs --check`。
   ⚠️ 比對只守得住數字與字串，**動畫對不對只能實看**（圖鑑的開發模式）。 */


/* ── BOSS-14 WAAGE 天秤 ── 刻意不對稱：一邊是 4.4 單位高的盾，一邊是 6 單位的砲。
   P1 橫樑水平，盾永遠擋在砲的另一側 —— 你只能繞到砲那邊，那裡最危險。
   P2 盾被打掉，橫樑往砲那側整個傾倒，桅杆核心露出來。
   P3 兩臂都垂下，橫樑高速旋轉 —— 天秤變成連枷。 */
export function buildWaage(){
  var GLA = glowMat(TINT.amb.hex, 2.2), GLV = glowMat(TINT.vio.hex, 2.4);
  var root = new THREE.Group(), i;
  var mast = boneG(root, 0, 0.80, 0);
  mast.add(bevel(M.dark,  2.40, 1.40, 2.40, [0,0,0]));
  for (i = 0; i < 3; i++){
    var a = i*Math.PI * 2/3 + 0.5;
    mast.add(mesh(G.cyl, M.armor, [0.54,2.40,0.54],
      [Math.sin(a)*1.40, -0.50, Math.cos(a)*1.40], [0.55*Math.cos(a),0,-0.55*Math.sin(a)]));
  }
  mast.add(mesh(G.bev, M.armor, [1.50*K_BEV,4.60,1.50*K_BEV], [0, 3.00, 0]));
  mast.add(rib (M.armor, 0.14, 4.20, 0.14, [0, 3.00, 0.80]));
  var cm = mesh(G.oct, GLV, [1.20,1.20,1.20], [0, 2.60, 0]);
  cm.userData.noMerge = 1; mast.add(cm);
  var beam = boneG(mast, 0, 5.40, 0);
  beam.add(mesh(G.box, M.armor, [10.4,0.44,0.70], [0,0,0]));
  beam.add(mesh(G.tor, M.trim,  [1.50,1.50,0.50], [0,0,0], [0,HP,0]));
  /* 左：盾 —— 唯一一片會擋住你視線的東西 */
  var sh = boneG(beam, -4.70, 0, 0);
  sh.add(mesh(G.cyl, M.dark, [0.50,1.60,0.50], [0,-0.80,0]));
  sh.add(plate(M.armor, 3.00, 4.40, 0.52, [0,-3.00, 0.10], 0));
  sh.add(rib  (M.armor, 0.16, 4.20, 0.16, [ 1.36,-3.00, 0.30]));
  sh.add(rib  (M.armor, 0.16, 4.20, 0.16, [-1.36,-3.00, 0.30]));
  /* 右：砲 */
  var gn = boneG(beam, 4.70, 0, 0);
  gn.add(mesh(G.cyl, M.dark,  [0.50,1.60,0.50], [0,-0.80,0]));
  gn.add(bevel(M.dark, 1.40, 1.40, 1.60, [0,-2.00,0]));
  gn.add(mesh(G.cyl, M.armor, [0.72,4.20,0.72], [0,-2.10,1.90], [HP,0,0]));
  var mz = mesh(G.tor, GLA, [1.10,1.10,0.55], [0,-2.10,4.10]);
  mz.userData.noMerge = 1; gn.add(mz);
  root.userData.rig = {mast:mast, beam:beam, sh:sh, gn:gn, cm:cm, mz:mz, GLA:GLA, GLV:GLV};
  return mergeBones(root);
}
export function updWaage(rig, o, dt){
  stages(rig, o, dt, 1.6);
  var s1 = rig.s1, s2 = rig.s2;
  rig.sh.visible = s1 < 0.5;
  rig.beam.rotation.z = -0.62*s1*(1-s2) + 0.10*Math.sin(o.t*0.8)*(1-s1);
  rig.beam.rotation.y += dt*(0.20 + 0.5*s1 + 4.2*s2);
  rig.beam.position.y = 5.40 - 0.50*s2;
  rig.gn.rotation.x = (-0.20 + o.tel*0.34 - o.atk*0.44)*(1-s2) + 1.25*s2;
  rig.sh.rotation.x = 0.10*Math.sin(o.t*1.1);
  rig.mast.rotation.z = 0.05*s1*(1-s2);
  rig.cm.scale.setScalar((0.4 + 0.9*s1 + 0.4*s2)*(1 + 0.12*Math.sin(o.t*3) + 0.6*o.tel));
  rig.mz.scale.setScalar(1 + 0.9*o.tel + 2.2*o.atk*(1-o.atk));
  rig.GLA.emissiveIntensity = 2.2 + 3.0*o.tel + 6*o.atk*(1-o.atk);
  rig.GLV.emissiveIntensity = 0.6 + 2.4*s1 + 2.4*s2;
}